import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import {
  listReadingPeriods,
  listPeriodVariableCharges,
  createPeriodVariableCharges,
  deletePeriodVariableCharge,
  listExpenseConcepts,
  listContracts,
  type ReadingPeriodItem,
  type PeriodVariableChargeItem,
  type ExpenseConceptItem,
} from "@/lib/api";

type ContractOption = { id: string; contractNumber: string; customerName: string };

export default function GastosVariablesPage() {
  const { session } = useSession();

  const [periods, setPeriods] = useState<ReadingPeriodItem[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [charges, setCharges] = useState<PeriodVariableChargeItem[]>([]);
  const [concepts, setConcepts] = useState<ExpenseConceptItem[]>([]);
  const [contracts, setContracts] = useState<ContractOption[]>([]);

  const [contractQuery, setContractQuery] = useState("");
  const [selectedContractId, setSelectedContractId] = useState("");
  const [selectedConceptId, setSelectedConceptId] = useState("");
  const [amount, setAmount] = useState("");
  const [filterQuery, setFilterQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    void listReadingPeriods(session).then((p) => {
      const sorted = [...p].sort((a, b) => b.periodCode.localeCompare(a.periodCode));
      setPeriods(sorted);
      if (sorted.length > 0) setSelectedPeriodId(sorted[0].id);
    });
    void listExpenseConcepts(session).then((c) => {
      const variable = c.filter((x) => x.conceptType === "variable" && x.status === "active");
      setConcepts(variable);
      if (variable.length > 0) setSelectedConceptId(variable[0].id);
    });
    void listContracts(session).then((c) =>
      setContracts(c.map((x) => ({ id: x.id, contractNumber: x.contractNumber, customerName: x.customerName ?? "" }))),
    );
  }, [session]);

  const loadCharges = useCallback(async () => {
    if (!session || !selectedPeriodId) return;
    setLoading(true);
    setError(null);
    try {
      setCharges(await listPeriodVariableCharges(session, selectedPeriodId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar gastos");
    } finally {
      setLoading(false);
    }
  }, [session, selectedPeriodId]);

  useEffect(() => { void loadCharges(); }, [loadCharges]);

  const filteredContracts = useMemo(() => {
    const q = contractQuery.trim().toLowerCase();
    if (!q) return contracts;
    return contracts.filter(
      (c) => c.contractNumber.toLowerCase().includes(q) || c.customerName.toLowerCase().includes(q),
    );
  }, [contracts, contractQuery]);

  const filteredCharges = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return charges;
    return charges.filter(
      (c) => c.customerName.toLowerCase().includes(q) || c.contractNumber.toLowerCase().includes(q) || c.expenseConceptName.toLowerCase().includes(q),
    );
  }, [charges, filterQuery]);

  const handleAdd = async () => {
    if (!session || !selectedPeriodId || !selectedContractId || !selectedConceptId) {
      setError("Selecciona período, contrato y tipo de gasto.");
      return;
    }
    const value = parseFloat(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Ingresa un valor mayor a 0.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createPeriodVariableCharges(session, {
        readingPeriodId: selectedPeriodId,
        charges: [{ contractId: selectedContractId, expenseConceptId: selectedConceptId, amount: value }],
      });
      setAmount("");
      setSelectedContractId("");
      setContractQuery("");
      await loadCharges();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al agregar gasto");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!session) return;
    setDeletingId(id);
    try {
      await deletePeriodVariableCharge(session, id);
      await loadCharges();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al eliminar gasto");
    } finally {
      setDeletingId(null);
    }
  };

  const selectedPeriod = periods.find((p) => p.id === selectedPeriodId);
  const totalAmount = filteredCharges.reduce((s, c) => s + c.amount, 0);

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Gastos Variables</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gestiona los gastos variables por contrato para cada período de facturación.</p>
      </div>

      {/* Selector de período */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <i className="ri-calendar-2-line text-gray-400" />
          <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Período</label>
          <select
            value={selectedPeriodId}
            onChange={(e) => setSelectedPeriodId(e.target.value)}
            className="flex-1 max-w-xs border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
          >
            <option value="">Selecciona período</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{p.periodCode}</option>
            ))}
          </select>
          {selectedPeriod && (
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              selectedPeriod.status === "open" ? "bg-green-100 text-green-700" :
              selectedPeriod.status === "closed" ? "bg-gray-100 text-gray-600" :
              "bg-amber-100 text-amber-700"
            }`}>
              {selectedPeriod.status === "open" ? "Abierto" : selectedPeriod.status === "closed" ? "Cerrado" : selectedPeriod.status}
            </span>
          )}
        </div>
        {charges.length > 0 && (
          <div className="text-sm text-gray-500">
            <span className="font-semibold text-gray-800">{charges.length}</span> gasto{charges.length !== 1 ? "s" : ""} ·{" "}
            <span className="font-semibold text-gray-800">${totalAmount.toLocaleString("es-CL")}</span> total
          </div>
        )}
      </div>

      {/* Formulario agregar */}
      {selectedPeriod?.status === "open" && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-gray-700">Agregar gasto variable</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="md:col-span-2">
              <input
                value={contractQuery}
                onChange={(e) => setContractQuery(e.target.value)}
                placeholder="Buscar por N° contrato o nombre cliente"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>
            <div>
              <select
                value={selectedContractId}
                onChange={(e) => setSelectedContractId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              >
                <option value="">Selecciona contrato</option>
                {filteredContracts.map((c) => (
                  <option key={c.id} value={c.id}>{c.contractNumber} - {c.customerName || "Sin nombre"}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={selectedConceptId}
                onChange={(e) => setSelectedConceptId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              >
                <option value="">Tipo de gasto</option>
                {concepts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Valor"
              className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? "Guardando..." : "Agregar gasto"}
            </button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {/* Tabla de gastos */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-gray-700">Detalle de gastos del período</p>
          <input
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            placeholder="Filtrar por nombre o N° contrato"
            className="w-64 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
          />
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">
            <i className="ri-loader-4-line animate-spin mr-2" />Cargando...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Contrato</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Gasto</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Valor</th>
                  {selectedPeriod?.status === "open" && (
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Acción</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredCharges.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                      {selectedPeriodId ? "Sin gastos variables para este período." : "Selecciona un período para ver los gastos."}
                    </td>
                  </tr>
                ) : filteredCharges.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-sm text-gray-700">{entry.customerName || "-"}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{entry.contractNumber || "-"}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{entry.expenseConceptName || "-"}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium text-gray-800">${entry.amount.toLocaleString("es-CL")}</td>
                    {selectedPeriod?.status === "open" && (
                      <td className="px-4 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => void handleDelete(entry.id)}
                          disabled={deletingId === entry.id}
                          className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                        >
                          {deletingId === entry.id ? "Eliminando..." : "Eliminar"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {filteredCharges.length > 0 && (
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr>
                    <td colSpan={3} className="px-4 py-2.5 text-sm font-semibold text-gray-600">Total</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-gray-800 text-right">${totalAmount.toLocaleString("es-CL")}</td>
                    {selectedPeriod?.status === "open" && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
